import { Component, OnInit, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { JjamesButtonComponent, JJamesPaginationConfig, JJamesPaginatorComponent, printReceiptSlip } from 'shared-lib';
import {
    ObligationStatus,
    PaymentObligationService,
    PaymentProviderKind,
    PaymentService,
    PaymentStatus,
    SortOrder,
    TerminalCaptureDto
} from '../../../api/gen';

export type PaymentSourceType = 'CardTerminal' | 'Stripe' | 'Im30' | 'Coins' | 'Credit' | 'Obligation' | 'Unknown';

export interface UnifiedPaymentItem {
    id: string;
    kind: 'direct_session' | 'sweep_obligation';
    sourceType: PaymentSourceType;
    sourceLabelKey: string;
    sourceIcon: string;
    date: string | undefined;
    licensePlate: string | undefined;
    parkingSpaceName: string | undefined;
    parkingSpaceId: string | undefined;
    parkingProcessId: string | undefined;
    stayStartedAt: string | undefined;
    stayEndedAt: string | undefined;
    stayDuration: string | null;
    amount: number;
    netPaid: number;
    currency: string;
    status: string;
    statusBadgeClass: string;
    statusLabel: string;
    reason?: string | null;
    providerChargeId?: string | null;
    receiptUrl?: string | null;
    isFiscal?: boolean | null;
    fiscalReceiptStatus?: string | null;
    receiptText?: string | null;
    /**
     * The terminal-of-record capture, carried whole rather than flattened.
     *
     * Everything below the four fields above used to be dropped in this mapping, so the Belegnummer,
     * the terminal, the fiscal transaction id and the refund-bound flag were on the wire and
     * unreachable from any template — which is why diagnosing the 2026-08-26 incident meant reading
     * Azure logs. Null for Stripe and for sweep obligations, which have no capture.
     */
    terminalCapture?: TerminalCaptureDto | null;
}

@Component({
    selector: 'app-all-payments-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesPaginatorComponent,
        TranslatePipe
    ],
    templateUrl: './all-payments-list.component.html',
    styleUrl: './all-payments-list.component.scss'
})
export class AllPaymentsListComponent implements OnInit {
    rowStatusClass(item: UnifiedPaymentItem): string {
        const status = (item.status || '').toLowerCase();
        switch (status) {
            case 'paid': return 'status-row-paid';
            case 'failed': return 'status-row-failed';
            case 'refunded': return 'status-row-refunded';
            case 'cancelled':
            case 'canceled':
            case 'waived': return 'status-row-cancelled';
            case 'open':
            case 'pending':
            case 'initiated': return 'status-row-pending';
            case 'expired': return 'status-row-expired';
            case 'heldforreview': return 'status-row-refunded';
            default: return '';
        }
    }
    /** Site narrowing from parent */
    readonly parkingSpaceId = input<string>('');

    private paymentService = inject(PaymentService);
    private obligationService = inject(PaymentObligationService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    readonly isLoading = signal(false);
    readonly isExporting = signal(false);

    /** Raw combined items fetched from backend */
    readonly allItems = signal<UnifiedPaymentItem[]>([]);

    /** Filters */
    readonly sourceFilter = signal<PaymentSourceType | ''>('');
    readonly statusFilter = signal<string>('');
    readonly plateFilter = signal<string>('');

    /** Pagination */
    readonly pageIndex = signal(0);
    readonly pageSize = signal(20);

    /** Expanded row tracking */
    readonly expandedRowIds = signal<Set<string>>(new Set<string>());

    readonly availableSources: { key: PaymentSourceType; labelKey: string }[] = [
        { key: 'CardTerminal', labelKey: 'allPayments.source_CardTerminal' },
        { key: 'Stripe', labelKey: 'allPayments.source_Stripe' },
        { key: 'Im30', labelKey: 'allPayments.source_Im30' },
        { key: 'Coins', labelKey: 'allPayments.source_Coins' },
        { key: 'Credit', labelKey: 'allPayments.source_Credit' },
        { key: 'Obligation', labelKey: 'allPayments.source_Obligation' }
    ];

    readonly availableStatuses: { key: string; label: string }[] = [
        { key: 'Paid', label: 'Paid' },
        { key: 'Open', label: 'Unpaid / Open' },
        { key: 'Pending', label: 'Pending / In Progress' },
        { key: 'Failed', label: 'Failed' },
        { key: 'Refunded', label: 'Refunded' },
        { key: 'Cancelled', label: 'Cancelled' },
        { key: 'Waived', label: 'Forgiven' },
        { key: 'Expired', label: 'Expired' }
    ];

    /** Filtered items after applying source, status, and plate search */
    readonly filteredItems = computed<UnifiedPaymentItem[]>(() => {
        let items = this.allItems();
        const source = this.sourceFilter();
        const status = this.statusFilter();
        const plate = this.plateFilter().trim().toUpperCase().replace(/[\s-]/g, '');

        if (source) {
            items = items.filter(i => i.sourceType === source);
        }

        if (status) {
            items = items.filter(i => {
                if (status === 'Paid') return i.status === 'Paid';
                if (status === 'Open') return i.status === 'Open' || i.status === 'HeldForReview';
                if (status === 'Pending') return i.status === 'Pending' || i.status === 'Initiated';
                if (status === 'Failed') return i.status === 'Failed';
                if (status === 'Refunded') return i.status === 'Refunded';
                if (status === 'Cancelled') return i.status === 'Cancelled';
                if (status === 'Waived') return i.status === 'Waived';
                if (status === 'Expired') return i.status === 'Expired';
                return i.status === status;
            });
        }

        if (plate) {
            items = items.filter(i => {
                const itemPlate = (i.licensePlate || '').toUpperCase().replace(/[\s-]/g, '');
                return itemPlate.includes(plate);
            });
        }

        return items;
    });

    /** Current slice of items for pagination */
    readonly pagedItems = computed<UnifiedPaymentItem[]>(() => {
        const items = this.filteredItems();
        const start = this.pageIndex() * this.pageSize();
        return items.slice(start, start + this.pageSize());
    });

    readonly totalFilteredCount = computed(() => this.filteredItems().length);

    /** Financial Aggregates across the entire set */
    readonly totalTransactionsCount = computed(() => this.allItems().length);

    readonly totalDirectTakings = computed(() => {
        return this.allItems()
            .filter(i => i.kind === 'direct_session' && i.status === 'Paid')
            .reduce((sum, i) => sum + i.netPaid, 0);
    });

    readonly totalSweepReceivables = computed(() => {
        return this.allItems()
            .filter(i => i.kind === 'sweep_obligation')
            .reduce((sum, i) => sum + i.amount, 0);
    });

    readonly totalNetCollected = computed(() => {
        return this.allItems()
            .filter(i => i.status === 'Paid')
            .reduce((sum, i) => sum + i.netPaid, 0);
    });

    readonly overallSuccessRate = computed(() => {
        const items = this.allItems();
        if (items.length === 0) return '—';
        const paidCount = items.filter(i => i.status === 'Paid').length;
        const rate = (paidCount / items.length) * 100;
        return `${rate.toFixed(1)}%`;
    });

    readonly pagination = computed<JJamesPaginationConfig>(() => ({
        currentPage: this.pageIndex(),
        totalPages: Math.max(1, Math.ceil(this.totalFilteredCount() / this.pageSize())),
        pageSize: this.pageSize(),
        totalItems: this.totalFilteredCount(),
        itemsPerPage: this.pageSize(),
        pageSizeOptions: [10, 20, 50, 100]
    }));

    private readonly reloadOnSiteChange = effect(() => {
        this.parkingSpaceId();

        untracked(() => {
            this.pageIndex.set(0);
            this.load();
        });
    });

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.isLoading.set(true);
        const siteId = this.parkingSpaceId() || undefined;

        // Concurrently fetch direct payment sessions and late-payment sweep obligations
        forkJoin({
            sessions: this.paymentService.paymentsEnrichedGet(
                undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                1, 200, siteId
            ),
            obligations: this.obligationService.paymentObligationsEnrichedGet(
                undefined, undefined, undefined, undefined, undefined,
                0, 200, 'CreatedAt', SortOrder.Descending, siteId
            )
        }).subscribe({
            next: ({ sessions, obligations }) => {
                const unified: UnifiedPaymentItem[] = [];

                // 1. Process Direct Payment Sessions
                for (const s of (sessions?.items ?? [])) {
                    const sourceType = this.resolveSessionSourceType(s.providerKind);
                    const isRefunded = s.hasRefund === true;
                    const net = s.netPaidAmount ?? (s.status === PaymentStatus.Paid ? (s.amount ?? 0) : 0);

                    unified.push({
                        id: s.id || `session-${Math.random()}`,
                        kind: 'direct_session',
                        sourceType,
                        sourceLabelKey: `allPayments.source_${sourceType}`,
                        sourceIcon: this.resolveSourceIcon(sourceType),
                        date: s.paidAt || s.createdAt || undefined,
                        licensePlate: s.licensePlate || undefined,
                        parkingSpaceName: s.parkingSpaceName || undefined,
                        parkingSpaceId: s.parkingSpaceId || undefined,
                        parkingProcessId: s.parkingProcessId || undefined,
                        stayStartedAt: s.stayStartedAt || undefined,
                        stayEndedAt: s.stayEndedAt || undefined,
                        stayDuration: this.formatDuration(s.stayDuration),
                        amount: s.amount ?? 0,
                        netPaid: net,
                        currency: s.currency || 'EUR',
                        status: isRefunded ? 'Refunded' : (s.status || 'Initiated'),
                        statusBadgeClass: this.resolveStatusBadgeClass(isRefunded ? 'Refunded' : (s.status || 'Initiated')),
                        statusLabel: isRefunded ? 'Refunded' : (s.status || 'Initiated'),
                        reason: s.hasRefund ? 'Refund recorded against payment transaction.' : null,
                        providerChargeId: s.providerChargeId || s.id,
                        receiptUrl: s.receiptUrl || s.terminalCapture?.fiscalReceiptUrl,
                        isFiscal: s.isFiscal ?? s.terminalCapture?.fiscalRegistered ?? (s.receiptUrl?.includes('efsta')),
                        fiscalReceiptStatus: s.fiscalReceiptStatus || s.terminalCapture?.fiscalReceiptStatus,
                        receiptText: s.receiptText || s.terminalCapture?.receiptText,
                        terminalCapture: s.terminalCapture ?? null
                    });
                }

                // 2. Process Late-Payment Sweep Obligations
                for (const o of (obligations?.items ?? [])) {
                    unified.push({
                        id: o.id || `obligation-${Math.random()}`,
                        kind: 'sweep_obligation',
                        sourceType: 'Obligation',
                        sourceLabelKey: 'allPayments.source_Obligation',
                        sourceIcon: 'receipt_long',
                        date: o.createdAt || undefined,
                        licensePlate: o.licensePlate || undefined,
                        parkingSpaceName: o.parkingSpaceName || undefined,
                        parkingSpaceId: o.parkingSpaceId || undefined,
                        parkingProcessId: o.parkingProcessId || undefined,
                        stayStartedAt: o.startedAt || undefined,
                        stayEndedAt: o.endedAt || undefined,
                        stayDuration: this.formatDuration(o.stayDuration),
                        amount: o.amount ?? 0,
                        netPaid: o.netPaidAmount ?? (o.status === ObligationStatus.Paid ? (o.amount ?? 0) : 0),
                        currency: o.currency || 'EUR',
                        status: o.status || 'Open',
                        statusBadgeClass: this.resolveStatusBadgeClass(o.status || 'Open'),
                        statusLabel: o.status === ObligationStatus.Open ? 'Unpaid (Sweep)' : (o.status || 'Open'),
                        reason: o.reason || null,
                        providerChargeId: o.providerChargeId || null,
                        receiptUrl: o.receiptUrl,
                        isFiscal: o.isFiscal,
                        fiscalReceiptStatus: o.fiscalReceiptStatus,
                        receiptText: o.receiptText
                    });
                }

                // Sort unified items descending by date
                unified.sort((a, b) => {
                    const timeA = a.date ? new Date(a.date).getTime() : 0;
                    const timeB = b.date ? new Date(b.date).getTime() : 0;
                    return timeB - timeA;
                });

                this.allItems.set(unified);
                this.isLoading.set(false);
            },
            error: (err) => {
                console.error('Failed to load unified all payments', err);
                this.allItems.set([]);
                this.isLoading.set(false);
                this.snackBar.open('Could not load unified payment attempts.', 'Dismiss', { duration: 5000 });
            }
        });
    }

    applyFilters(): void {
        this.pageIndex.set(0);
    }

    clearFilters(): void {
        this.sourceFilter.set('');
        this.statusFilter.set('');
        this.plateFilter.set('');
        this.applyFilters();
    }

    onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
    }

    toggleRowExpand(id: string): void {
        const current = new Set(this.expandedRowIds());
        if (current.has(id)) {
            current.delete(id);
        } else {
            current.add(id);
        }
        this.expandedRowIds.set(current);
    }

    isRowExpanded(id: string): boolean {
        return this.expandedRowIds().has(id);
    }

    readonly selectedReceipt = signal<{
        title: string;
        text?: string;
        plate?: string;
        amount?: number;
        currency?: string;
        date?: string;
        /** Belegnummer — names the printed sheet and the saved PDF. */
        beleg?: string;
    } | null>(null);

    getReceiptInfo(item: UnifiedPaymentItem): {
        label: string;
        icon: string;
        buttonClass: string;
        tooltip: string;
        url?: string;
        isExternal: boolean;
    } | null {
        const receiptUrl = item.receiptUrl;
        const isFiscal = item.isFiscal ?? (receiptUrl?.includes('efsta'));
        const receiptText = item.receiptText;

        if (receiptUrl) {
            if (isFiscal || receiptUrl.includes('efsta')) {
                return {
                    label: 'EFSTA Bill',
                    icon: 'verified',
                    buttonClass: 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100/90 shadow-2xs',
                    tooltip: 'EFSTA Fiscal Receipt (RKSV Compliant)',
                    url: receiptUrl,
                    isExternal: true
                };
            }
            if (receiptUrl.includes('stripe')) {
                return {
                    label: 'Stripe Receipt',
                    icon: 'receipt_long',
                    buttonClass: 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100/90 shadow-2xs',
                    tooltip: 'Stripe Payment Receipt',
                    url: receiptUrl,
                    isExternal: true
                };
            }
            return {
                label: 'Receipt / Invoice',
                icon: 'description',
                buttonClass: 'bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100/90 shadow-2xs',
                tooltip: 'Digital Receipt / Invoice',
                url: receiptUrl,
                isExternal: true
            };
        }

        if (receiptText) {
            return {
                label: 'Card Receipt',
                icon: 'receipt_long',
                buttonClass: 'bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200/80 shadow-2xs',
                tooltip: 'The card slip the terminal printed (Händlerbeleg / Kundenbeleg)',
                isExternal: false
            };
        }

        // Deliberately nothing here for a Paid row with neither a URL nor a slip.
        //
        // This used to return a "Stripe Receipt" / "Terminal / EFSTA" button with no url and no text,
        // purely because the row was Paid. openReceipt() then found nothing to open and answered with
        // "No digital receipt URL available for this record" — a button whose only behaviour was to
        // apologise. It reads as a broken link, and it hid the real cause: the backend populates
        // ReceiptUrl solely from TerminalCapture.FiscalReceiptUrl, so a Stripe session (which has no
        // capture) can never have one. Stripe's receipt lives on the Charge and we do not store it yet.
        //
        // Showing the "none" state instead is honest, and it stops this looking like a UI fault.
        return null;
    }

    openReceipt(item: UnifiedPaymentItem, event?: Event): void {
        event?.stopPropagation();
        if (item.receiptUrl) {
            window.open(item.receiptUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        if (item.receiptText) {
            this.selectedReceipt.set({
                title: 'Terminal Receipt Slip',
                text: item.receiptText,
                plate: item.licensePlate,
                amount: item.netPaid ?? item.amount,
                currency: item.currency || 'EUR',
                date: item.date ? new Date(item.date).toLocaleString() : undefined
            });
            return;
        }

        this.snackBar.open('No digital receipt URL available for this record.', 'Close', { duration: 3000 });
    }

    /**
     * Always shows the card slip the terminal printed — never the fiscal bill.
     *
     * openReceipt() checks receiptUrl first and returns, so on a capture that HAS an EFSTA bill it
     * opened the bill and the slip was unreachable: two differently-labelled buttons, one outcome.
     * These are two different documents. The EFSTA bill is the fiscal record; this is the card
     * receipt (Händlerbeleg / Kundenbeleg) with the Beleg-Nr, masked PAN, GEN.NR. and amount — the
     * one the acquirer asks for when a cardholder disputes a charge.
     */
    showTerminalSlip(item: UnifiedPaymentItem, event?: Event): void {
        event?.stopPropagation();

        const text = item.terminalCapture?.receiptText || item.receiptText;
        if (!text) {
            this.snackBar.open('No card receipt was stored for this payment.', 'Close', { duration: 3000 });
            return;
        }

        this.selectedReceipt.set({
            title: 'Card Receipt',
            text,
            plate: item.licensePlate,
            amount: item.netPaid ?? item.amount,
            currency: item.currency || 'EUR',
            date: item.date ? new Date(item.date).toLocaleString() : undefined,
            beleg: item.terminalCapture?.externalPaymentId ?? undefined
        });
    }

    closeReceiptModal(): void {
        this.selectedReceipt.set(null);
    }

    /** Prints the open slip — see {@link printReceiptSlip} for why it goes through an iframe. */
    printReceiptModal(): void {
        const receipt = this.selectedReceipt();
        if (!receipt?.text) return;
        printReceiptSlip({ ...receipt, text: receipt.text });
    }

    copyToClipboard(text?: string, label?: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            this.snackBar.open(`${label || 'ID'} copied to clipboard`, 'OK', { duration: 2500 });
        });
    }

    exportCsv(): void {
        if (this.isExporting()) return;
        const rows = this.filteredItems();
        if (rows.length === 0) {
            this.snackBar.open(this.translate.instant('allPayments.emptyBody'), 'Dismiss', { duration: 4000 });
            return;
        }

        this.isExporting.set(true);
        try {
            const csv = this.toCsv(rows);
            const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `all-payments-tries-${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            this.snackBar.open(
                this.translate.instant('allPayments.exportDone', { count: rows.length }),
                'Close',
                { duration: 4000 }
            );
        } catch (err) {
            console.error('Failed to export unified payments', err);
            this.snackBar.open('Failed to export CSV.', 'Dismiss', { duration: 5000 });
        } finally {
            this.isExporting.set(false);
        }
    }

    private toCsv(rows: UnifiedPaymentItem[]): string {
        const headers = [
            'Date & Time', 'Origin / Resource', 'Licence Plate', 'Parking Space',
            'Stay Entry', 'Stay Exit', 'Stay Duration', 'Gross Amount', 'Net Paid',
            'Currency', 'Status', 'Parking Process ID', 'Transaction ID', 'Notes / Reason'
        ];

        const quote = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

        const lines = rows.map(r => [
            quote(r.date),
            quote(r.sourceType === 'Obligation' ? 'Late Payment Sweep' : r.sourceType),
            quote(r.licensePlate),
            quote(r.parkingSpaceName),
            quote(r.stayStartedAt),
            quote(r.stayEndedAt),
            quote(r.stayDuration),
            (r.amount ?? 0).toFixed(2),
            (r.netPaid ?? 0).toFixed(2),
            quote(r.currency || 'EUR'),
            quote(r.status),
            quote(r.parkingProcessId),
            quote(r.id),
            quote(r.reason)
        ].join(','));

        return [headers.join(','), ...lines].join('\r\n');
    }

    private resolveSessionSourceType(kind?: PaymentProviderKind): PaymentSourceType {
        if (kind === PaymentProviderKind.CardTerminal) return 'CardTerminal';
        if (kind === PaymentProviderKind.Stripe) return 'Stripe';
        if (kind === PaymentProviderKind.Im30) return 'Im30';
        if (kind === PaymentProviderKind.CoinAcceptor) return 'Coins';
        if (kind === PaymentProviderKind.Wallet) return 'Credit';
        return 'Unknown';
    }

    private resolveSourceIcon(source: PaymentSourceType): string {
        switch (source) {
            case 'CardTerminal': return 'credit_card';
            case 'Stripe': return 'smartphone';
            case 'Im30': return 'local_parking';
            case 'Coins': return 'toll';
            case 'Credit': return 'account_balance_wallet';
            case 'Obligation': return 'receipt_long';
            default: return 'help_outline';
        }
    }

    private resolveStatusBadgeClass(status: string): string {
        switch (status) {
            case 'Paid':
                return 'attr-pill--green';
            case 'Open':
            case 'Initiated':
            case 'Pending':
                return 'attr-pill--blue';
            case 'HeldForReview':
            case 'Refunded':
                return 'attr-pill--amber';
            case 'Failed':
            case 'Expired':
                return 'attr-pill--rose';
            case 'Waived':
            case 'Cancelled':
                return 'attr-pill--slate';
            default:
                return 'attr-pill--blue';
        }
    }

    private formatDuration(raw?: string | null): string | null {
        if (!raw) return null;
        const match = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})/.exec(raw);
        if (!match) return null;
        const days = Number(match[1] ?? 0);
        const hours = days * 24 + Number(match[2]);
        const minutes = Number(match[3]);
        return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
    }
}
